import * as Y from 'yjs';
import { uid, ts, esc } from './utils.js';
import { initStore, getDoc, onStoreChange, getNodes, getNode, getNodeMap, getRootOrderArray, getSetting, getSelectedId, getExpanded, getTrashEntries, addNode, renameNode as storeRenameNode, updateContent, setSelectedId, setExpanded, setSetting, moveNode, sendToTrash, restoreFromTrash, permanentDeleteTrash, emptyTrash as storeEmptyTrash, migrateLegacySettingsIfNeeded, getActiveProfile, getAutoProfile, getProfileOverride, setProfileOverride, getProfileBreakpoints, setBreakpoints, getSettingFromProfile, setSettingInProfile } from './store.js';
import { createLocalPersistence } from './yjs/local-persistence.js';
import { SupabaseProvider } from './yjs/supabase-provider.js';
import { restoreSession, signOut, getToken, getUser, setUser, isLoggedIn, setAuthChangeHandler, handleOAuthCallback, startGoogleLogin } from './auth.js';
import { importFromFile, exportToFile, detectLegacyData } from './import.js';

// ── Icons (SVG for consistent color across all devices) ──
const ICON_FOLDER = '<svg width="14" height="14" viewBox="0 0 24 24" fill="#e8a838" stroke="#d49520" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>';
const ICON_MEMO = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6aaa6a" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>';

// ── State ──
let trashOpen = false;
let trashFolderExpanded = new Set();
let searchQuery = '';
let saveTimer = null;
let dragId = null;
let dropTarget = null;
let expandTimer = null;
let expandTargetId = null;
let editorH = 0;
let editorW = 0;
let layout = 'bottom'; // 'bottom' | 'right'
let provider = null;
let suppressRender = false;
let userTypingTimer = null;
let userIsTyping = false;
let activeTextObserver = null;
let activeText = null;
let peerSelections = []; // [{selectedId, device}]
let renderTimer = null;

// ── DOM refs ──
const $ = (id) => document.getElementById(id);

// ── Init ──
async function init() {
  // Legacy per-tab settings (sessionStorage) → responsive profiles (localStorage).
  // Runs only once; subsequent loads are no-ops.
  migrateLegacySettingsIfNeeded();

  const doc = initStore();

  // Local persistence (always on)
  const localPersistence = createLocalPersistence(doc);

  // Wait for y-indexeddb to load
  await new Promise((resolve) => {
    localPersistence.on('synced', resolve);
  });

  // Y.Doc 変更を監視（observer は connectCloud より前に取り付けないと、
  // 初期同期で来た更新を取りこぼして「F5しないと反映されない」になる）
  let renderPending = false;
  onStoreChange(() => {
    if (suppressRender) return;
    if (userIsTyping) {
      const selId = getSelectedId();
      if (selId) {
        const n = getNode(selId);
        if (n) {
          const labelEl = document.querySelector(`.node-row[data-id="${selId}"] .node-label`);
          if (labelEl) labelEl.textContent = n.name || '';
        }
      }
      return;
    }
    if (renderPending) return;
    renderPending = true;
    clearTimeout(renderTimer);
    renderTimer = setTimeout(() => {
      renderPending = false;
      renderTree();
      renderTrash();
      syncSettingsUI();
      updatePeerIndicators();
    }, layout === 'fullscreen' ? 300 : 50);
  });

  // Handle Stripe upgrade redirect
  if (location.search.includes('upgraded=1')) {
    history.replaceState(null, '', '/');
    showUpgradeProgress();
    const token = getToken();
    let upgraded = false;
    if (token) {
      for (let i = 0; i < 10; i++) {
        try {
          const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            setUser(data.user);
            if (data.user.plan === 'paid') { upgraded = true; break; }
          }
        } catch {}
        await new Promise(r => setTimeout(r, 2000));
      }
    }
    hideUpgradeProgress(upgraded);
    updateUserUI();
  }

  // Handle OAuth callback (redirect from Google)
  const oauthHandled = await handleOAuthCallback();

  // Restore auth session
  const wasLoggedIn = oauthHandled || restoreSession();

  // If logged in, connect to cloud (non-blocking)
  if (wasLoggedIn && isLoggedIn()) {
    connectCloud(doc);
    updateUserUI();
    hideLoginOverlay();
    // Refresh plan info from server (localStorage cache may be stale)
    const token = getToken();
    if (token) {
      fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.user) {
            setUser(data.user);
            updateUserUI();
          }
        })
        .catch(() => {});
    }
    // NOTE: ログイン済みユーザーには絶対にデモデータを自動生成しない。
    // 過去のインシデント(2026-05-20): 5秒タイムアウトで同期未完了のままデモ
    // 生成 → 既存メモと衝突して破壊。空に見えても「サーバーから取得できて
    // いないだけ」のことがあるため、空判定でのデモ作成は本質的に危険。
    // 代わりに、本当に空のときは UI 側の「メモがありません」表示と
    // 「新規メモを作成」ボタンに任せる。
  } else {
    updateUserUI();
    // Check for legacy data
    const hasLegacy = await detectLegacyData();
    if (hasLegacy) {
      // Will show import prompt after login
    }
  }

  // Auth state changes
  setAuthChangeHandler((event, user) => {
    if (event === 'signed_in') {
      connectCloud(doc);
      updateUserUI();
      hideLoginOverlay();
    } else if (event === 'signed_out') {
      disconnectCloud();
      updateUserUI();
    }
  });

  // Create demo data if empty (for non-logged-in users, immediate)
  if (!isLoggedIn()) {
    const nodes = getNodes();
    const rootOrder = getRootOrderArray();
    if (Object.keys(nodes).length === 0 && rootOrder.length === 0) {
      createDemoData();
    }
  }

  // Set initial layout — per responsive profile (S/M/L). Fallback to the legacy
  // memo_layout (single value for whole browser) for migration, then default.
  const defaultLayout = window.innerWidth <= 600 ? 'bottom' : 'bottom';
  layout = getSetting('layout') || localStorage.getItem('memo_layout') || defaultLayout;
  editorH = getSetting('editorH') || Math.floor(window.innerHeight * 0.38);
  editorW = parseInt(sessionStorage.getItem('memo_editorW')) || Math.floor(window.innerWidth * 0.45);
  applyLayout();
  // Restore fullscreen editor view
  if (layout === 'fullscreen' && sessionStorage.getItem('memo_fs_view') === 'editor') {
    const selId = getSelectedId();
    if (selId && getNode(selId)?.type === 'memo') {
      document.querySelector('.app').classList.add('fs-editor');
    }
  }

  // Render
  initSettingsDefaults();
  syncSettingsUI();
  applyEditorStyle();
  renderTree();
  renderTrash();

  const selectedId = getSelectedId();
  if (selectedId) {
    const node = getNode(selectedId);
    if (node?.type === 'memo') selectMemo(selectedId);
  }

  // (observer は connectCloud 前に既に取り付け済み)

  // Save tree scroll position continuously (beforeunload unreliable on mobile)
  $('treeArea').addEventListener('scroll', () => {
    sessionStorage.setItem('memo_treeScroll', $('treeArea').scrollTop);
  }, { passive: true });

  // Wire up UI events
  wireEvents();

  // First-time tutorial (after first login)
  if (isLoggedIn() && !localStorage.getItem('memo_tutorial_done')) {
    setTimeout(() => startTutorial(), 800);
  }
}

function connectCloud(doc) {
  if (provider) provider.destroy();
  provider = new SupabaseProvider(doc, {
    getToken,
    userId: getUser()?.id,
  });

  provider.on('status', ({ status }) => {
    const el = $('syncIndicator');
    if (status === 'connected') {
      el.className = 'sync-indicator';
      el.textContent = '';
    } else if (status === 'syncing') {
      // Only show during initial connect (not routine saves)
      if (!provider.synced) {
        el.className = 'sync-indicator syncing';
        el.textContent = '同期中';
      }
    } else if (status === 'error') {
      el.className = 'sync-indicator error';
      el.textContent = '同期エラー';
    } else {
      el.className = 'sync-indicator offline';
      el.textContent = 'オフライン';
    }
  });

  provider.on('presence', (peers) => {
    peerSelections = peers;
    updatePeerIndicators();
  });

  provider.on('auth-error', () => {
    // Token expired, try refresh
    import('./auth.js').then(({ refreshToken }) => refreshToken());
  });

  // 同期完了で明示的に再描画（observer が走ってもデバウンスで遅延しているとき
  // 用の保険。タブ復帰直後の表示更新の取りこぼしを防ぐ）
  provider.on('synced', () => {
    renderTree();
    renderTrash();
  });

  provider.on('data-loss-warning', ({ before, after }) => {
    // 同期で大幅なノード減少を検知。pre-sync-conflict スナップショットは
    // 既に provider 側で自動撮影済み。ユーザーに復元 UI への導線を案内。
    showWarning(
      `同期時にメモが ${before} 件から ${after} 件に大幅減少しました。` +
      `安全のため直前の状態をスナップショットに保存しました。` +
      `設定 → バックアップ・復元 から元に戻せます。`
    );
  });

  provider.connect();
}

function showWarning(msg) {
  const el = document.getElementById('warningBanner');
  if (el) {
    el.textContent = msg;
    el.style.display = '';
    return;
  }
  // Fallback: create a sticky banner.
  const banner = document.createElement('div');
  banner.id = 'warningBanner';
  banner.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:9999;background:#fff4b8;color:#5a3d00;padding:10px 16px;border-bottom:1px solid #d9b800;font-size:12px;line-height:1.5;';
  banner.textContent = msg;
  const close = document.createElement('button');
  close.textContent = '×';
  close.style.cssText = 'float:right;background:transparent;border:0;font-size:18px;cursor:pointer;color:#5a3d00;';
  close.onclick = () => banner.remove();
  banner.prepend(close);
  document.body.prepend(banner);
}

function disconnectCloud() {
  if (provider) {
    provider.destroy();
    provider = null;
  }
  $('syncIndicator').className = 'sync-indicator offline';
}

// ── Login UI ──
function hideLoginOverlay() {
  document.documentElement.classList.remove('no-session');
  $('loginOverlay').classList.remove('visible');
  $('loginOverlay').classList.add('hidden');
}

function showLoginOverlay() {
  $('loginOverlay').classList.remove('hidden');
  $('loginOverlay').classList.add('visible');
}

function updateUserUI() {
  const user = getUser();
  if (user) {
    $('userAvatar').src = user.avatar || '';
    $('userAvatar').style.display = user.avatar ? '' : 'none';
    $('headerLoginBtn').style.display = 'none';
    $('accountSettings').style.display = '';
    $('loginSettings').style.display = 'none';
    $('settingsName').textContent = user.name || '';
    $('settingsEmail').textContent = user.email || '';
    $('settingsAvatar').src = user.avatar || '';

    // Plan UI
    const isPaid = user.plan === 'paid';
    const badge = $('planBadge');
    badge.textContent = isPaid ? 'Pro' : 'Free';
    badge.className = 'plan-badge ' + (isPaid ? 'paid' : 'free');
    const hBadge = $('headerPlanBadge');
    hBadge.textContent = isPaid ? 'Pro' : 'Free';
    hBadge.className = 'plan-badge ' + (isPaid ? 'paid' : 'free');
    hBadge.style.display = '';
    $('planInfo').textContent = isPaid
      ? (user.cancelAtPeriodEnd ? '解約予定（期間終了まで利用可）' : 'メモ無制限')
      : 'メモ 10個まで';
    $('btnUpgrade').style.display = isPaid ? 'none' : '';
    $('btnCancelPlan').style.display = (isPaid && !user.cancelAtPeriodEnd) ? '' : 'none';
  } else {
    $('userAvatar').style.display = 'none';
    $('headerPlanBadge').style.display = 'none';
    $('headerLoginBtn').style.display = '';
    $('accountSettings').style.display = 'none';
    $('loginSettings').style.display = '';
  }
}

// ── Demo data ──
function createDemoData() {
  const w = addNode('folder', null, '仕事');
  const pj = addNode('folder', w, 'プロジェクトA');
  addNode('memo', pj, 'タスクリスト');
  addNode('memo', pj, 'ミーティングメモ');
  addNode('memo', w, 'アイデア帳');
  const p = addNode('folder', null, '個人');
  addNode('memo', p, '買い物リスト');
  addNode('memo', null, 'メモ帳');
  const expanded = new Set([w, pj, p]);
  setExpanded(expanded);
}

// ── Helpers ──
function getVisibleNodeIds() {
  const expanded = getExpanded();
  const result = [];
  function walk(ids) {
    for (const id of getSortedIds(ids)) {
      const n = getNode(id);
      if (!n) continue;
      result.push(id);
      if (n.type === 'folder' && expanded.has(id)) walk(n.childOrder || []);
    }
  }
  walk(getRootOrderArray());
  return result;
}

function getOrder(parentId) {
  if (parentId) {
    const node = getNode(parentId);
    return node?.childOrder ?? [];
  }
  return getRootOrderArray();
}

function getSortedIds(ids) {
  const sort = getSetting('sort') || 'manual';
  if (sort === 'manual') return ids;
  const nodes = getNodes();
  return [...ids].sort((a, b) => {
    const na = nodes[a], nb = nodes[b];
    if (!na || !nb) return 0;
    if (na.type === 'folder' && nb.type !== 'folder') return -1;
    if (na.type !== 'folder' && nb.type === 'folder') return 1;
    if (sort === 'name') return (na.name || '').localeCompare(nb.name || '', 'ja');
    if (sort === 'created') return nb.createdAt - na.createdAt;
    if (sort === 'updated') return nb.updatedAt - na.updatedAt;
    return 0;
  });
}

function getDepth(id) {
  let d = 0;
  let n = getNode(id);
  while (n?.parentId) { d++; n = getNode(n.parentId); }
  return d;
}

function isDescOf(ancestor, id) {
  if (!id) return false;
  if (id === ancestor) return true;
  return isDescOf(ancestor, getNode(id)?.parentId);
}

function countMemos(nodeId) {
  const n = getNode(nodeId);
  if (!n) return 0;
  if (n.type === 'memo') return 1;
  return (n.childOrder || []).reduce((sum, cid) => sum + countMemos(cid), 0);
}

// ── Render tree ──
function renderTree() {
  if (searchQuery) { renderSearch(searchQuery); return; }
  const wrap = $('treeWrap');
  const savedScroll = $('treeArea').scrollTop;
  wrap.innerHTML = '';
  const rootIds = getRootOrderArray();
  if (rootIds.length === 0) {
    const empty = document.createElement('div');
    empty.style.cssText = 'padding:24px 12px;font-size:11px;color:#aaa;text-align:center;line-height:1.7;';
    empty.innerHTML = 'メモはまだありません<br>上の「＋メモ」ボタンから作成できます';
    wrap.appendChild(empty);
    return;
  }
  renderNodes(rootIds, wrap, 0);
  // Restore scroll position after DOM rebuild
  const stored = parseInt(sessionStorage.getItem('memo_treeScroll'));
  $('treeArea').scrollTop = stored || savedScroll;
}

function renderNodes(ids, container, d) {
  const expanded = getExpanded();
  const selectedId = getSelectedId();

  getSortedIds(ids).forEach((id) => {
    const n = getNode(id);
    if (!n) return;

    const row = document.createElement('div');
    row.className = 'node-row' + (id === selectedId ? ' is-selected' : '');
    row.dataset.id = id;

    const ind = document.createElement('span');
    ind.className = 'node-indent';
    ind.style.width = (d * 14 + 3) + 'px';
    row.appendChild(ind);

    const arr = document.createElement('span');
    arr.className = 'node-arrow' + (n.type !== 'folder' ? ' invisible' : '');
    arr.textContent = '\u25B6';
    if (n.type === 'folder') {
      if (expanded.has(id)) arr.classList.add('is-open');
      arr.addEventListener('click', (e) => { e.stopPropagation(); toggleExpand(id); });
    }
    row.appendChild(arr);

    const icon = document.createElement('span');
    icon.className = 'node-icon';
    icon.innerHTML = n.type === 'folder' ? ICON_FOLDER : ICON_MEMO;
    row.appendChild(icon);

    const label = document.createElement('span');
    label.className = 'node-label';
    label.textContent = n.name || '';
    row.appendChild(label);

    if (n.type === 'folder') {
      const cnt = countMemos(id);
      if (cnt > 0) {
        const cntEl = document.createElement('span');
        cntEl.className = 'folder-count';
        cntEl.textContent = `(${cnt})`;
        row.appendChild(cntEl);
      }
      const renameBtn = document.createElement('span');
      renameBtn.className = 'folder-rename-btn';
      renameBtn.textContent = '✎';
      renameBtn.title = '名前変更';
      renameBtn.addEventListener('click', (e) => { e.stopPropagation(); startEdit(id, label); });
      row.appendChild(renameBtn);
    }

    function selectFolder() {
      setSelectedId(id);
      document.querySelectorAll('.node-row.is-selected').forEach((r) => r.classList.remove('is-selected'));
      row.classList.add('is-selected');
      $('editorEmpty').style.display = '';
      $('editorMain').classList.remove('visible');
    }

    row.addEventListener('click', () => {
      if (n.type === 'folder') {
        selectFolder();
        toggleExpand(id);
      } else {
        selectMemo(id);
        showFsEditor();
      }
    });

    // Touch: tap selects folder and toggles expand
    if (n.type === 'folder') {
      let touchMoved = false;
      row.addEventListener('touchstart', () => { touchMoved = false; }, { passive: true });
      row.addEventListener('touchmove', () => { touchMoved = true; }, { passive: true });
      row.addEventListener('touchend', (e) => {
        if (touchMoved || touchDragging) return;
        e.preventDefault();
        selectFolder();
        toggleExpand(id);
      });
    }

    // Drag — desktop: native drag, mobile: long-press touch drag
    if (!('ontouchstart' in window)) {
      row.draggable = true;
      row.addEventListener('dragstart', (e) => { dragId = id; e.dataTransfer.effectAllowed = 'move'; setTimeout(() => row.classList.add('is-dragging'), 0); });
      row.addEventListener('dragend', () => { dragId = null; dropTarget = null; clearDragStyles(); clearTimeout(expandTimer); expandTimer = null; expandTargetId = null; hideTip(); });
      row.addEventListener('dragover', (e) => handleDragOver(e, id));
      row.addEventListener('dragleave', (e) => { if (!row.contains(e.relatedTarget)) { clearRowStyle(row); dropTarget = null; hideTip(); } });
      row.addEventListener('drop', (e) => handleDrop(e, id));
    }
    initTouchDrag(row, id);

    container.appendChild(row);

    if (n.type === 'folder') {
      const ch = document.createElement('div');
      ch.className = 'node-children';
      ch.dataset.cid = id;
      if (expanded.has(id)) {
        renderNodes(n.childOrder || [], ch, d + 1);
        ch.style.height = 'auto';
      }
      container.appendChild(ch);
    }
  });
}

function mkBtn(label, title, fn, isDel) {
  const btn = document.createElement('button');
  btn.className = 'node-btn' + (isDel ? ' del' : '');
  btn.title = title;
  btn.textContent = label;
  btn.addEventListener('click', (e) => { e.stopPropagation(); fn(); });
  return btn;
}

// ── Expand / Collapse ──
function toggleExpand(id) {
  const expanded = getExpanded();
  const ch = document.querySelector(`.node-children[data-cid="${id}"]`);
  const arr = document.querySelector(`.node-row[data-id="${id}"] .node-arrow`);

  if (expanded.has(id)) {
    expanded.delete(id);
    if (arr) arr.classList.remove('is-open');
    if (ch) {
      ch.style.height = ch.scrollHeight + 'px';
      requestAnimationFrame(() => { ch.style.height = '0'; });
    }
  } else {
    expanded.add(id);
    if (arr) arr.classList.add('is-open');
    if (ch) {
      ch.innerHTML = '';
      const node = getNode(id);
      renderNodes(node?.childOrder || [], ch, getDepth(id) + 1);
      const h = ch.scrollHeight;
      ch.style.height = '0';
      requestAnimationFrame(() => {
        ch.style.height = h + 'px';
        setTimeout(() => { if (getExpanded().has(id)) ch.style.height = 'auto'; }, 170);
      });
    }
  }
  // Suppress re-render so DOM isn't destroyed (preserves dblclick on labels)
  suppressRender = true;
  setExpanded(expanded);
  suppressRender = false;
}

// ── Select memo ──
function selectMemo(id) {
  // Remove previous Y.Text observer
  if (activeText && activeTextObserver) {
    activeText.unobserve(activeTextObserver);
    activeText = null;
    activeTextObserver = null;
  }

  setSelectedId(id);
  provider?.trackSelection(id);
  const n = getNode(id);
  if (!n) return;

  document.querySelectorAll('.node-row.is-selected').forEach((r) => r.classList.remove('is-selected'));
  document.querySelector(`.node-row[data-id="${id}"]`)?.classList.add('is-selected');

  $('editorEmpty').style.display = 'none';
  $('editorMain').classList.add('visible');
  $('editorBody').value = n.content || '';
  applyEditorStyle();

  // Directly observe Y.Text for remote changes → instant editor update
  const nodeMap = getNodeMap(id);
  if (nodeMap) {
    activeText = nodeMap.get('content');
    if (activeText) {
      activeTextObserver = (_event, transaction) => {
        // Only react to remote changes (not our own saves)
        if (!transaction.origin) return; // local transact() has origin=null
        if (userIsTyping) return;
        const editor = $('editorBody');
        const remote = activeText.toString();
        if (editor.value !== remote) {
          const start = editor.selectionStart;
          const end = editor.selectionEnd;
          editor.value = remote;
          editor.selectionStart = Math.min(start, remote.length);
          editor.selectionEnd = Math.min(end, remote.length);
        }
      };
      activeText.observe(activeTextObserver);
    }
  }
}

function updatePeerIndicators() {
  // Remove old indicators
  document.querySelectorAll('.peer-indicator').forEach((el) => el.remove());
  document.querySelectorAll('.node-row.peer-active').forEach((el) => el.classList.remove('peer-active'));

  for (const peer of peerSelections) {
    if (!peer.selectedId) continue;
    const row = document.querySelector(`.node-row[data-id="${peer.selectedId}"]`);
    if (row) {
      row.classList.add('peer-active');
      const dot = document.createElement('span');
      dot.className = 'peer-indicator';
      dot.title = `${peer.device} が閲覧中`;
      row.appendChild(dot);
    }
  }
}

function hideEditor() {
  setSelectedId(null);
  provider?.trackSelection(null);
  $('editorEmpty').style.display = '';
  $('editorMain').classList.remove('visible');
}

// ── Auto-save ──
function schedSave() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    const id = getSelectedId();
    if (!id || !getNode(id)) return;
    const content = $('editorBody').value;
    updateContent(id, content);
  }, 400);
}

// ── Add folder / memo ──
function addFolder(parentId) {
  let targetParent = parentId;
  let afterId = null;
  const selectedId = getSelectedId();

  if (targetParent === null && selectedId) {
    const sel = getNode(selectedId);
    if (sel?.type === 'folder') {
      // フォルダ選択中 → その中の先頭に追加
      targetParent = selectedId;
      const expanded = getExpanded();
      expanded.add(selectedId);
      setExpanded(expanded);
    } else if (sel) {
      // メモ選択中 → 同じ親の、選択中アイテムの直後に追加
      targetParent = sel.parentId;
      afterId = selectedId;
    }
  }

  const id = addNode('folder', targetParent, undefined, afterId);
  renderTree();
  const row = document.querySelector(`.node-row[data-id="${id}"]`);
  if (row) startEdit(id, row.querySelector('.node-label'));
}

const FREE_MEMO_LIMIT = 10;

async function addMemo(parentId) {
  // Check free plan limit
  let u = getUser();
  if (u?.plan !== 'paid') {
    const nodes = getNodes();
    const memoCount = Object.values(nodes).filter(n => n.type === 'memo').length;
    if (memoCount >= FREE_MEMO_LIMIT) {
      // Re-fetch plan in case webhook updated it after upgrade
      const token = getToken();
      if (token) {
        try {
          const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const data = await res.json();
            setUser(data.user);
            u = data.user;
          }
        } catch {}
      }
      if (u?.plan !== 'paid') {
        showUpgradeModal();
        return;
      }
      // Plan was updated — continue to add memo
      updateUserUI();
    }
  }

  let targetParent = parentId;
  let afterId = null;
  const selectedId = getSelectedId();

  if (targetParent === null && selectedId) {
    const sel = getNode(selectedId);
    if (sel?.type === 'folder') {
      targetParent = selectedId;
      const expanded = getExpanded();
      expanded.add(selectedId);
      setExpanded(expanded);
    } else if (sel) {
      targetParent = sel.parentId;
      afterId = selectedId;
    }
  }

  const id = addNode('memo', targetParent, undefined, afterId);
  renderTree();
  if (layout === 'fullscreen') {
    // Just add to tree, don't switch to editor
    setSelectedId(id);
    document.querySelectorAll('.node-row.is-selected').forEach((r) => r.classList.remove('is-selected'));
    document.querySelector(`.node-row[data-id="${id}"]`)?.classList.add('is-selected');
  } else {
    selectMemo(id);
    if (!('ontouchstart' in window)) $('editorBody').focus();
  }
}

function showUpgradeModal() {
  $('upgradeModal').classList.add('open');
}

function hideUpgradeModal() {
  $('upgradeModal').classList.remove('open');
}

function showUpgradeProgress() {
  const m = $('upgradeModal');
  const card = m.querySelector('.upgrade-card');
  card.innerHTML = '<div class="upgrade-icon">&#x23F3;</div>'
    + '<h2 class="upgrade-title">プランを確認中...</h2>'
    + '<p class="upgrade-desc">Stripeの決済を確認しています。<br>しばらくお待ちください。</p>';
  m.classList.add('open');
}

function hideUpgradeProgress(success) {
  const m = $('upgradeModal');
  const card = m.querySelector('.upgrade-card');
  if (success) {
    card.innerHTML = '<div class="upgrade-icon">&#x1F389;</div>'
      + '<h2 class="upgrade-title">アップグレード完了!</h2>'
      + '<p class="upgrade-desc">Proプランが有効になりました。<br>メモを無制限に作成できます。</p>'
      + '<button class="upgrade-btn" onclick="document.getElementById(\'upgradeModal\').classList.remove(\'open\')">OK</button>';
  } else {
    card.innerHTML = '<div class="upgrade-icon">&#x26A0;&#xFE0F;</div>'
      + '<h2 class="upgrade-title">確認に時間がかかっています</h2>'
      + '<p class="upgrade-desc">決済は完了していますが、反映に<br>少し時間がかかる場合があります。<br>ページを再読み込みしてください。</p>'
      + '<button class="upgrade-btn" onclick="location.reload()">再読み込み</button>';
  }
}

// ── Backup / Restore ─────────────────────────────────────────────────

const KIND_LABELS = {
  'auto-3min':          '3分自動',
  'auto-hourly':        '1時間自動',
  'auto-daily':         '日次自動',
  'manual':             '手動',
  'pre-compact':        '圧縮前',
  'pre-sync-conflict':  '同期競合前',
  'pre-empty-trash':    'ゴミ箱空にする前',
  'pre-restore':        '復元前',
};

function formatBytes(bytes) {
  if (!bytes || bytes < 1024) return (bytes || 0) + 'B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB';
  return (bytes / 1024 / 1024).toFixed(1) + 'MB';
}

function openBackupModal() {
  $('backupModal').classList.add('open');
  $('backupMask').classList.add('open');
  loadSnapshotList();
}
function closeBackupModal() {
  $('backupModal').classList.remove('open');
  $('backupMask').classList.remove('open');
}
function openPreviewModal() {
  $('previewModal').classList.add('open');
  $('previewMask').classList.add('open');
}
function closePreviewModal() {
  $('previewModal').classList.remove('open');
  $('previewMask').classList.remove('open');
}

async function loadSnapshotList() {
  const listEl = $('backupList');
  listEl.innerHTML = '<div class="backup-empty">読込中...</div>';
  const token = getToken();
  if (!token) { listEl.innerHTML = '<div class="backup-empty">ログインが必要です</div>'; return; }
  try {
    const res = await fetch('/api/yjs/snapshots', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('list failed: ' + res.status);
    const { snapshots } = await res.json();
    // Usage line
    const usageEl = $('backupUsage');
    if (usageEl) {
      if (!snapshots || snapshots.length === 0) {
        usageEl.textContent = '';
      } else {
        const totalBytes = snapshots.reduce((s, x) => s + (x.size_bytes || 0), 0);
        const byKind = {};
        for (const s of snapshots) byKind[s.kind] = (byKind[s.kind] || 0) + 1;
        const breakdown = ['auto-3min', 'auto-hourly', 'auto-daily'].map(k =>
          `${KIND_LABELS[k]}:${byKind[k] || 0}`).join(' / ');
        usageEl.textContent = `合計 ${snapshots.length}件 / ${formatBytes(totalBytes)}  (${breakdown})`;
      }
    }
    if (!snapshots || snapshots.length === 0) {
      listEl.innerHTML = '<div class="backup-empty">スナップショットはまだありません</div>';
      return;
    }
    listEl.innerHTML = '';
    for (const s of snapshots) {
      const row = document.createElement('div');
      row.className = 'backup-row';
      const date = new Date(s.created_at).toLocaleString('ja-JP');
      const label = KIND_LABELS[s.kind] || s.kind;
      const sizeKB = (s.size_bytes / 1024).toFixed(1);
      row.innerHTML =
        `<span class="backup-kind kind-${s.kind}">${esc(label)}</span>` +
        `<div class="backup-row-meta">` +
        `<div class="backup-row-date">${esc(date)}</div>` +
        `<div class="backup-row-stat">ノード ${s.node_count}件 / ${sizeKB}KB${s.note ? ' / ' + esc(s.note) : ''}</div>` +
        `</div>` +
        `<div class="backup-row-actions">` +
        `<button class="sort-pill" data-act="preview">プレビュー</button>` +
        `<button class="sort-pill" data-act="delete">削除</button>` +
        `</div>`;
      row.querySelector('[data-act="preview"]').addEventListener('click', () => previewSnapshot(s.id, label, date));
      row.querySelector('[data-act="delete"]').addEventListener('click', () => deleteSnapshot(s.id));
      listEl.appendChild(row);
    }
  } catch (e) {
    console.error(e);
    listEl.innerHTML = '<div class="backup-empty">読込失敗: ' + esc(e.message) + '</div>';
  }
}

async function takeManualSnapshotAndReload() {
  if (!provider?.takeManualSnapshot) { alert('クラウド未接続のためスナップショットできません'); return; }
  const note = prompt('メモ（任意）:', '') || '';
  try {
    const r = await provider.takeManualSnapshot(note);
    if (r?.ok) {
      await loadSnapshotList();
    } else {
      alert('スナップショット失敗');
    }
  } catch (e) {
    alert('エラー: ' + e.message);
  }
}

async function deleteSnapshot(id) {
  if (!confirm('このスナップショットを削除しますか？')) return;
  const token = getToken();
  const res = await fetch('/api/yjs/snapshots/' + id, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.ok) loadSnapshotList();
  else alert('削除失敗');
}

let _currentPreview = null;

async function previewSnapshot(id, label, date) {
  $('previewTitle').textContent = `${label} ${date}`;
  $('previewBody').textContent = '読込中...';
  openPreviewModal();
  const token = getToken();
  try {
    const res = await fetch('/api/yjs/snapshots/' + id, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('preview failed: ' + res.status);
    const data = await res.json();
    if (!data.state) throw new Error('snapshot has no state');
    const bytes = b64ToBytes(data.state);
    const previewDoc = new Y.Doc();
    Y.applyUpdate(previewDoc, bytes);
    const snapshot = docToSnapshot(previewDoc);
    _currentPreview = { id, snapshot, state: bytes };
    $('previewBody').textContent = formatSnapshotPreview(snapshot);
  } catch (e) {
    $('previewBody').textContent = 'エラー: ' + e.message;
    _currentPreview = null;
  }
}

function b64ToBytes(s) {
  const bin = atob(s);
  const a = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i);
  return a;
}

function docToSnapshot(d) {
  const nodesMap = d.getMap('nodes');
  const rootOrder = d.getArray('rootOrder');
  const trashMap = d.getMap('trash');
  const nodes = {};
  nodesMap.forEach((nm, id) => {
    if (!nm) return;
    const type = nm.get('type');
    const n = { id: nm.get('id'), type, name: nm.get('name'), parentId: nm.get('parentId'),
      createdAt: nm.get('createdAt'), updatedAt: nm.get('updatedAt') };
    if (type === 'memo') { const t = nm.get('content'); n.content = t && t.toString ? t.toString() : ''; }
    else { const co = nm.get('childOrder'); n.childOrder = co && co.toArray ? co.toArray() : []; }
    nodes[id] = n;
  });
  const trash = {};
  trashMap.forEach((v, k) => { try { trash[k] = JSON.parse(v); } catch { trash[k] = v; } });
  return { rootOrder: rootOrder.toArray ? rootOrder.toArray() : [], nodes, trash };
}

function formatSnapshotPreview(s) {
  const memos = Object.values(s.nodes).filter(n => n.type === 'memo');
  const folders = Object.values(s.nodes).filter(n => n.type === 'folder');
  const lines = [];
  lines.push(`生存ノード: メモ ${memos.length}件, フォルダ ${folders.length}件 / ゴミ箱: ${Object.keys(s.trash).length}件`);
  lines.push('');
  memos.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  for (const m of memos.slice(0, 50)) {
    lines.push('─'.repeat(40));
    lines.push(`【${m.name || '(無題)'}】 ${m.updatedAt ? new Date(m.updatedAt).toLocaleString('ja-JP') : ''}`);
    lines.push('');
    lines.push(m.content || '(空)');
    lines.push('');
  }
  if (memos.length > 50) lines.push(`... ほか ${memos.length - 50} 件`);
  return lines.join('\n');
}

// Hook preview action buttons (set once on first use).
let _previewActionsBound = false;
function bindPreviewActions() {
  if (_previewActionsBound) return;
  _previewActionsBound = true;
  $('previewDownload').addEventListener('click', () => {
    if (!_currentPreview) return;
    const blob = new Blob([JSON.stringify(_currentPreview.snapshot, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `memo-snapshot-${_currentPreview.id}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  });
  $('previewRestore').addEventListener('click', async () => {
    if (!_currentPreview) return;
    if (!confirm('このスナップショットを現在の状態に復元しますか？\n\n現在の状態は自動で「復元前」スナップショットに退避されます。\n復元後は自動で再読込されます。')) return;
    const token = getToken();
    const res = await fetch('/api/yjs/snapshots/' + _currentPreview.id + '/restore', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      alert('復元しました。ページを再読込します。');
      location.reload();
    } else {
      alert('復元失敗: ' + res.status);
    }
  });
}
// Bind once at module load (next tick to ensure DOM ready)
setTimeout(bindPreviewActions, 0);

// ── ローカル自動エクスポート（最終保険）─────────────────────────────────
// タブを閉じる/隠す時に Y.Doc を JSON 化して localStorage に保存。
// 直近3日分だけ保持。サーバー無関係に直近の状態を取り出せる。
const LOCAL_BACKUP_PREFIX = 'memo_local_backup_';
const LOCAL_BACKUP_KEEP = 3;

function saveLocalBackup() {
  try {
    const d = getDoc();
    if (!d) return;
    const nodes = d.getMap('nodes');
    if (nodes.size === 0) return; // 空 doc は保存しない（誤上書き防止）
    const snapshot = docToSnapshot(d);
    const today = new Date();
    const key = LOCAL_BACKUP_PREFIX + today.toISOString().slice(0, 10);
    const payload = { savedAt: today.toISOString(), snapshot };
    localStorage.setItem(key, JSON.stringify(payload));
    // Prune: keep only the LOCAL_BACKUP_KEEP most recent dates
    const keys = Object.keys(localStorage)
      .filter(k => k.startsWith(LOCAL_BACKUP_PREFIX))
      .sort((a, b) => b.localeCompare(a));
    for (const k of keys.slice(LOCAL_BACKUP_KEEP)) localStorage.removeItem(k);
  } catch (e) {
    // QuotaExceeded or other — silently ignore. This is just a safety net.
    console.warn('[local backup] save failed:', e?.message);
  }
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') saveLocalBackup();
});
window.addEventListener('pagehide', saveLocalBackup);
window.addEventListener('beforeunload', saveLocalBackup);

// ── Responsive profile auto-switch ────────────────────────────────────
// On window resize, if the active profile changes (because we crossed a
// breakpoint), re-apply that profile's settings to the UI.
let _lastActiveProfile = null;
let _resizeProfileTimer = null;
function _checkProfileChange() {
  const cur = getActiveProfile();
  if (cur !== _lastActiveProfile) {
    _lastActiveProfile = cur;
    // Re-apply layout per the new profile (サブ/メイン で別レイアウト保持)
    const newLayout = getSetting('layout');
    if (newLayout && newLayout !== layout) {
      layout = newLayout;
      if (typeof applyLayout === 'function') applyLayout();
    }
    if (typeof applyEditorStyle === 'function') applyEditorStyle();
    if (typeof syncSettingsUI === 'function') syncSettingsUI();
    if (typeof renderTree === 'function') renderTree();
  }
}
window.addEventListener('resize', () => {
  if (_resizeProfileTimer) clearTimeout(_resizeProfileTimer);
  _resizeProfileTimer = setTimeout(_checkProfileChange, 150);
});
// Also re-check when storage event fires (another tab updated profiles/breakpoints)
window.addEventListener('storage', (e) => {
  if (e.key === 'memo_profiles' || e.key === 'memo_profile_breakpoints') {
    _checkProfileChange();
    if (typeof applyEditorStyle === 'function') applyEditorStyle();
    if (typeof syncSettingsUI === 'function') syncSettingsUI();
  }
});

async function upgradeToProPlan() {
  const token = getToken();
  if (!token) return;
  try {
    const res = await fetch('/api/stripe/checkout', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (data.url) location.href = data.url;
  } catch (e) {
    console.error('Checkout error:', e);
  }
}

// ── Rename ──
function startEdit(id, labelEl) {
  const orig = getNode(id)?.name ?? '';
  const inp = document.createElement('input');
  inp.className = 'node-label-input';
  inp.value = orig;
  labelEl.replaceWith(inp);
  // Cancel any pending re-render and suppress new ones while editing
  clearTimeout(renderTimer);
  suppressRender = true;
  inp.focus();
  inp.select();
  let committed = false;
  const done = () => {
    if (committed) return;
    committed = true;
    suppressRender = false;
    storeRenameNode(id, inp.value);
    renderTree();
  };
  inp.addEventListener('blur', done);
  inp.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); inp.blur(); }
    if (e.key === 'Escape') { inp.value = orig; inp.blur(); }
  });
}

// ── Search ──
function getPath(id) {
  const parts = [];
  let n = getNode(id);
  while (n?.parentId) {
    n = getNode(n.parentId);
    if (n) parts.unshift(n.name);
  }
  return parts.join(' / ');
}

function renderSearch(q) {
  const wrap = $('treeWrap');
  wrap.innerHTML = '';
  const lq = q.toLowerCase();
  const nodes = getNodes();
  const selectedId = getSelectedId();
  const matches = Object.values(nodes).filter((n) =>
    n.type === 'memo' && ((n.name || '').toLowerCase().includes(lq) || (n.content || '').toLowerCase().includes(lq))
  );
  if (matches.length === 0) {
    const empty = document.createElement('div');
    empty.style.cssText = 'padding:20px 12px;font-size:11px;color:#ccc;text-align:center;';
    empty.textContent = '一致するメモがありません';
    wrap.appendChild(empty);
    return;
  }
  matches.forEach((n) => {
    const row = document.createElement('div');
    row.className = 'node-row' + (n.id === selectedId ? ' is-selected' : '');
    row.dataset.id = n.id;

    const ind = document.createElement('span');
    ind.className = 'node-indent';
    ind.style.width = '3px';
    row.appendChild(ind);

    const arrEl = document.createElement('span');
    arrEl.className = 'node-arrow invisible';
    arrEl.textContent = '\u25B6';
    row.appendChild(arrEl);

    const iconEl = document.createElement('span');
    iconEl.className = 'node-icon';
    iconEl.innerHTML = ICON_MEMO;
    row.appendChild(iconEl);

    const labelEl = document.createElement('span');
    labelEl.className = 'node-label';
    labelEl.textContent = n.name || '';
    row.appendChild(labelEl);

    const path = getPath(n.id);
    if (path) {
      const pathEl = document.createElement('span');
      pathEl.style.cssText = 'font-size:9px;color:#ccc;flex-shrink:0;margin-right:4px;white-space:nowrap;';
      pathEl.textContent = path;
      row.appendChild(pathEl);
    }

    row.addEventListener('click', () => { selectMemo(n.id); showFsEditor(); });
    wrap.appendChild(row);
  });
}

// ── Touch Drag (long-press) ──
let touchDragTimer = null;
let touchDragging = false;

function initTouchDrag(row, id) {
  let startX, startY;

  row.addEventListener('touchstart', (e) => {
    if (touchDragging) return;
    const t = e.touches[0];
    startX = t.clientX; startY = t.clientY;
    touchDragTimer = setTimeout(() => {
      touchDragging = true;
      dragId = id;
      row.classList.add('is-dragging');
      navigator.vibrate?.(30);
    }, 500);
  }, { passive: true });

  row.addEventListener('touchmove', (e) => {
    const t = e.touches[0];
    // Cancel long-press if moved before drag starts
    if (!touchDragging) {
      if (Math.abs(t.clientX - startX) > 8 || Math.abs(t.clientY - startY) > 8) {
        clearTimeout(touchDragTimer);
      }
      return;
    }
    e.preventDefault();
    // Find row under finger
    const el = document.elementFromPoint(t.clientX, t.clientY);
    const targetRow = el?.closest('.node-row');
    if (targetRow && targetRow.dataset.id !== dragId) {
      const targetId = targetRow.dataset.id;
      clearDragStyles();
      row.classList.add('is-dragging');
      const tn = getNode(targetId);
      if (!tn) return;
      const { top, height } = targetRow.getBoundingClientRect();
      const rel = t.clientY - top;
      if (tn.type === 'folder' && rel > height * 0.2 && rel < height * 0.8) {
        targetRow.classList.add('drag-into');
        dropTarget = { type: 'into', id: targetId };
      } else if (rel <= height * 0.5) {
        targetRow.classList.add('drag-before');
        dropTarget = { type: 'before', id: targetId };
      } else {
        targetRow.classList.add('drag-after');
        dropTarget = { type: 'after', id: targetId };
      }
    }
  }, { passive: false });

  row.addEventListener('touchend', () => {
    clearTimeout(touchDragTimer);
    if (touchDragging) {
      if (dragId && dropTarget) {
        const { type, id: tId } = dropTarget;
        if (type === 'into') {
          moveNode(dragId, tId, null);
        } else {
          const tn = getNode(tId);
          if (tn) {
            const sib = getOrder(tn.parentId);
            if (type === 'before') moveNode(dragId, tn.parentId || null, tId);
            else moveNode(dragId, tn.parentId || null, sib[sib.indexOf(tId) + 1] || null);
          }
        }
        renderTree();
      }
      dragId = null;
      dropTarget = null;
      clearDragStyles();
      touchDragging = false;
    }
  });

  row.addEventListener('touchcancel', () => {
    clearTimeout(touchDragTimer);
    dragId = null;
    dropTarget = null;
    clearDragStyles();
    touchDragging = false;
  });
}

// ── Drag & Drop ──
function hideTip() { $('dragTip').style.display = 'none'; }
function clearDragStyles() { document.querySelectorAll('.node-row').forEach(clearRowStyle); }
function clearRowStyle(r) { r.classList.remove('is-dragging', 'drag-into', 'drag-before', 'drag-after'); }

function handleDragOver(e, targetId) {
  e.preventDefault();
  if (!dragId || dragId === targetId) return;
  const tn = getNode(targetId);
  if (!tn) return;
  clearDragStyles();
  const row = document.querySelector(`.node-row[data-id="${targetId}"]`);
  if (!row) return;
  const { top, height } = row.getBoundingClientRect();
  const rel = e.clientY - top;
  let mode;

  if (tn.type === 'folder' && rel > height * 0.2 && rel < height * 0.8) {
    row.classList.add('drag-into');
    dropTarget = { type: 'into', id: targetId };
    mode = 'into';
    if (expandTargetId !== targetId) {
      clearTimeout(expandTimer);
      expandTargetId = targetId;
      expandTimer = setTimeout(() => {
        const expanded = getExpanded();
        if (!expanded.has(targetId)) { expanded.add(targetId); setExpanded(expanded); renderTree(); }
      }, 700);
    }
  } else {
    if (expandTargetId !== null) { clearTimeout(expandTimer); expandTimer = null; expandTargetId = null; }
    if (rel <= height * 0.5) {
      row.classList.add('drag-before');
      dropTarget = { type: 'before', id: targetId };
      mode = 'before';
    } else {
      row.classList.add('drag-after');
      dropTarget = { type: 'after', id: targetId };
      mode = 'after';
    }
  }
  document.querySelector(`.node-row[data-id="${dragId}"]`)?.classList.add('is-dragging');
  e.dataTransfer.dropEffect = 'move';

  const tip = $('dragTip');
  tip.textContent = mode === 'into' ? 'フォルダに移動' : mode === 'before' ? '前に挿入' : '後に挿入';
  tip.style.left = (e.clientX + 14) + 'px';
  tip.style.top = (e.clientY + 14) + 'px';
  tip.style.display = '';
}

function handleDrop(e) {
  e.preventDefault();
  clearTimeout(expandTimer);
  expandTimer = null;
  expandTargetId = null;
  hideTip();
  if (!dragId || !dropTarget) { clearDragStyles(); return; }
  const { type, id: tId } = dropTarget;
  if (type === 'into') {
    moveNode(dragId, tId, null);
  } else {
    const tn = getNode(tId);
    if (!tn) return;
    const sib = getOrder(tn.parentId);
    if (type === 'before') moveNode(dragId, tn.parentId || null, tId);
    else moveNode(dragId, tn.parentId || null, sib[sib.indexOf(tId) + 1] || null);
  }
  dragId = null;
  dropTarget = null;
  clearDragStyles();
  renderTree();
}

// ── Trash UI ──
function renderTrash() {
  const entries = getTrashEntries();
  const count = Object.keys(entries).length;
  const countEl = $('trashCount');
  countEl.textContent = count;
  countEl.style.display = count > 0 ? '' : 'none';
  $('trashEmptyBtn').style.display = (count > 0 && trashOpen) ? '' : 'none';

  const listEl = $('trashList');
  if (!trashOpen) { listEl.style.display = 'none'; return; }
  listEl.style.display = '';
  listEl.innerHTML = '';

  if (count === 0) {
    listEl.innerHTML = '<div style="padding:5px 20px;font-size:11px;color:#ccc;">ゴミ箱は空です</div>';
    return;
  }

  const now = ts();
  Object.entries(entries).sort(([, a], [, b]) => b.deletedAt - a.deletedAt).forEach(([entryId, entry]) => {
    const daysLeft = Math.max(0, 30 - Math.floor((now - entry.deletedAt) / 86400000));
    const isFolder = entry.type === 'folder';
    const hasChildren = isFolder && Object.keys(entry.subtree).length > 1;
    const isExpanded = trashFolderExpanded.has(entryId);

    const item = document.createElement('div');
    item.className = 'trash-item';

    if (hasChildren) {
      const arrow = document.createElement('span');
      arrow.className = 'trash-fold-arrow' + (isExpanded ? ' open' : '');
      arrow.textContent = '\u25B6';
      arrow.addEventListener('click', (e) => {
        e.stopPropagation();
        if (trashFolderExpanded.has(entryId)) trashFolderExpanded.delete(entryId);
        else trashFolderExpanded.add(entryId);
        renderTrash();
      });
      item.appendChild(arrow);
    } else {
      const sp = document.createElement('span');
      sp.className = 'trash-fold-spacer';
      item.appendChild(sp);
    }

    item.insertAdjacentHTML('beforeend',
      `<span class="trash-item-icon">${isFolder ? ICON_FOLDER : ICON_MEMO}</span>` +
      `<span class="trash-item-name">${esc(entry.displayName)}</span>` +
      `<span class="trash-item-days">残${daysLeft}日</span>` +
      `<button class="trash-btn trash-btn-restore">復元</button>` +
      `<button class="trash-btn trash-btn-del">削除</button>`
    );
    item.querySelector('.trash-btn-restore').addEventListener('click', (e) => { e.stopPropagation(); restoreFromTrash(entryId); });
    item.querySelector('.trash-btn-del').addEventListener('click', (e) => { e.stopPropagation(); permanentDeleteTrash(entryId); });
    listEl.appendChild(item);

    if (isFolder && isExpanded) {
      renderTrashSubtree(entry.subtree, entryId, listEl, 0);
    }
  });
}

function renderTrashSubtree(subtree, nodeId, container, depth) {
  const n = subtree[nodeId];
  if (!n) return;
  (n.childOrder || []).forEach((cid) => {
    const child = subtree[cid];
    if (!child) return;
    const row = document.createElement('div');
    row.className = 'trash-sub-item';
    row.style.paddingLeft = (20 + depth * 14) + 'px';
    row.innerHTML =
      `<span class="trash-sub-icon">${child.type === 'folder' ? ICON_FOLDER : ICON_MEMO}</span>` +
      `<span class="trash-sub-name">${esc(child.name || '')}</span>`;
    container.appendChild(row);
    if (child.type === 'folder') renderTrashSubtree(subtree, cid, container, depth + 1);
  });
}

// ── Layout ──
function applyLayout() {
  const app = document.querySelector('.app');
  app.classList.remove('layout-right', 'layout-fullscreen');
  if (layout === 'right') app.classList.add('layout-right');
  else if (layout === 'fullscreen') app.classList.add('layout-fullscreen');

  // Reset all inline sizing first
  const ea = $('editorArea');
  ea.style.height = '';
  ea.style.width = '';
  $('treeArea').style.flex = '1';

  if (layout === 'right') {
    setEditorWidth(editorW);
  } else if (layout === 'bottom') {
    setEditorHeight(editorH);
  }
  // fullscreen: sizes managed by CSS flex
  syncLayoutPills();
}

function setLayout(newLayout) {
  layout = newLayout;
  // Store per responsive profile so サブ/メイン で別レイアウトを保持できる。
  setSetting('layout', layout);
  // Clear fullscreen editor state when switching away
  if (layout !== 'fullscreen') {
    document.querySelector('.app').classList.remove('fs-editor');
    sessionStorage.removeItem('memo_fs_view');
  }
  applyLayout();
}

function syncLayoutPills() {
  document.querySelectorAll('#layoutPills .sort-pill').forEach((p) =>
    p.classList.toggle('active', p.dataset.layout === layout)
  );
}

// ── Fullscreen view switching ──
function showFsEditor() {
  if (layout !== 'fullscreen') return;
  document.querySelector('.app').classList.add('fs-editor');
  sessionStorage.setItem('memo_fs_view', 'editor');
}

function showFsTree() {
  document.querySelector('.app').classList.remove('fs-editor');
  sessionStorage.setItem('memo_fs_view', 'tree');
}

function setEditorHeight(h) {
  editorH = Math.min(window.innerHeight - 120, Math.max(60, h));
  $('editorArea').style.height = editorH + 'px';
}

function setEditorWidth(w) {
  editorW = Math.min(window.innerWidth - 200, Math.max(200, w));
  $('editorArea').style.width = editorW + 'px';
}

// ── Split handle ──
function initSplitHandle() {
  const handle = $('splitHandle');
  let active = false, startPos = 0, startSize = 0;

  function isEffectiveRight() {
    return layout === 'right';
  }

  function onStart(clientX, clientY) {
    active = true;
    if (isEffectiveRight()) {
      startPos = clientX; startSize = editorW;
      document.body.style.cursor = 'col-resize';
    } else {
      startPos = clientY; startSize = editorH;
      document.body.style.cursor = 'row-resize';
    }
    handle.classList.add('active');
    document.body.style.userSelect = 'none';
  }

  function onMove(clientX, clientY) {
    if (!active) return;
    if (isEffectiveRight()) {
      setEditorWidth(startSize + (startPos - clientX));
    } else {
      setEditorHeight(startSize + (startPos - clientY));
    }
  }

  function onEnd() {
    if (!active) return;
    active = false;
    handle.classList.remove('active');
    document.body.style.cursor = document.body.style.userSelect = '';
    if (isEffectiveRight()) {
      sessionStorage.setItem('memo_editorW', editorW);
    } else {
      setSetting('editorH', editorH);
    }
  }

  // Mouse events
  handle.addEventListener('mousedown', (e) => { e.preventDefault(); onStart(e.clientX, e.clientY); });
  document.addEventListener('mousemove', (e) => onMove(e.clientX, e.clientY));
  document.addEventListener('mouseup', onEnd);

  // Touch events
  handle.addEventListener('touchstart', (e) => { e.preventDefault(); const t = e.touches[0]; onStart(t.clientX, t.clientY); }, { passive: false });
  document.addEventListener('touchmove', (e) => { if (!active) return; e.preventDefault(); const t = e.touches[0]; onMove(t.clientX, t.clientY); }, { passive: false });
  document.addEventListener('touchend', onEnd);
}

// ── Settings ──
function toggleSettings() {
  $('settingsPanel').classList.toggle('open');
  $('settingsMask').classList.toggle('open');
}

const SETTING_DEFAULTS = {
  treeFontSize: 11, treeLineHeight: 1.6,
  fontSize: 11, letterSpacing: 0, lineHeight: 1.65,
  sort: 'manual',
};
const SETTING_KEYS = Object.keys(SETTING_DEFAULTS);

function initSettingsDefaults() {
  for (const [k, v] of Object.entries(SETTING_DEFAULTS)) {
    if (getSetting(k) === null) setSetting(k, v);
  }
}

function resetSettingsToDefaults() {
  for (const [k, v] of Object.entries(SETTING_DEFAULTS)) setSetting(k, v);
  syncSettingsUI();
  applyEditorStyle();
  flashBtn($('btnResetSettings'), '✓ リセット');
}

function flashBtn(el, text) {
  const orig = el.textContent;
  el.textContent = text;
  el.style.background = '#4a8fdb';
  el.style.color = '#fff';
  el.style.borderColor = '#4a8fdb';
  setTimeout(() => { el.textContent = orig; el.style.background = ''; el.style.color = ''; el.style.borderColor = ''; }, 600);
}

function savePreset(slot) {
  const data = {};
  for (const k of SETTING_KEYS) data[k] = getSetting(k);
  localStorage.setItem('memo_preset_' + slot, JSON.stringify(data));
  updatePresetUI();
  flashBtn($('presetSave' + slot), '✓');
}

function loadPreset(slot) {
  const raw = localStorage.getItem('memo_preset_' + slot);
  if (!raw) return;
  const data = JSON.parse(raw);
  for (const [k, v] of Object.entries(data)) setSetting(k, v);
  syncSettingsUI();
  applyEditorStyle();
  flashBtn($('presetLoad' + slot), '✓');
}

function updatePresetUI() {
  for (let i = 1; i <= 3; i++) {
    const saved = !!localStorage.getItem('memo_preset_' + i);
    const btn = $('presetLoad' + i);
    if (btn) btn.disabled = !saved;
    const nameEl = $('presetName' + i);
    if (nameEl) nameEl.textContent = localStorage.getItem('memo_preset_name_' + i) || ('プリセット' + i);
  }
}

function startPresetRename(slot) {
  const nameEl = $('presetName' + slot);
  const orig = nameEl.textContent;
  const inp = document.createElement('input');
  inp.className = 'preset-name-input';
  inp.value = orig;
  nameEl.replaceWith(inp);
  inp.focus();
  inp.select();
  const done = () => {
    const val = inp.value.trim() || ('プリセット' + slot);
    localStorage.setItem('memo_preset_name_' + slot, val);
    const span = document.createElement('span');
    span.className = 'preset-name';
    span.id = 'presetName' + slot;
    span.title = 'クリックで名前変更';
    span.textContent = val;
    span.addEventListener('click', () => startPresetRename(slot));
    inp.replaceWith(span);
  };
  inp.addEventListener('blur', done);
  inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') inp.blur(); });
}

function syncSettingsUI() {
  $('slTreeFontSize').value = getSetting('treeFontSize') || 11;
  $('vTreeFontSize').textContent = (getSetting('treeFontSize') || 11) + 'px';
  $('slTreeLineHeight').value = getSetting('treeLineHeight') || 1.6;
  $('vTreeLineHeight').textContent = getSetting('treeLineHeight') || 1.6;
  $('slFontSize').value = getSetting('fontSize') || 11;
  $('vFontSize').textContent = (getSetting('fontSize') || 11) + 'px';
  $('slLetterSpacing').value = getSetting('letterSpacing') || 0;
  $('vLetterSpacing').textContent = (getSetting('letterSpacing') || 0) + 'px';
  $('slLineHeight').value = getSetting('lineHeight') || 1.65;
  $('vLineHeight').textContent = getSetting('lineHeight') || 1.65;
  document.querySelectorAll('.sort-pill[data-sort]').forEach((p) =>
    p.classList.toggle('active', p.dataset.sort === (getSetting('sort') || 'manual'))
  );
  applyTreeStyle();
  renderProfileSelector();
}

// ── Responsive profile UI ─────────────────────────────────────────────

const BP_MIN = 200;   // px
const BP_MAX = 1800;  // px
const PROFILE_NAMES = { S: '小', M: '中', L: '大' };

function pxToPct(px) {
  return Math.max(0, Math.min(100, ((px - BP_MIN) / (BP_MAX - BP_MIN)) * 100));
}
function pctToPx(pct) {
  return Math.round(BP_MIN + (pct / 100) * (BP_MAX - BP_MIN));
}

function renderProfileSelector() {
  const [b1, b2] = getProfileBreakpoints();
  const override = getProfileOverride(); // 'S'|'M'|'L'|null
  const auto = getAutoProfile();
  const active = override || auto;
  const w = window.innerWidth;

  // Tabs active state
  document.querySelectorAll('#profileTabs .sort-pill').forEach((p) => {
    const v = p.dataset.profile;
    p.classList.toggle('active', (v === 'auto' && !override) || v === override);
  });

  // Now indicator
  $('profileNow').textContent =
    `現在: ${w}px → ${PROFILE_NAMES[active]}` + (override ? '（手動）' : '');

  // Zone widths
  const pct1 = pxToPct(b1);
  const pct2 = pxToPct(b2);
  $('bpZoneS').style.left = '0%';
  $('bpZoneS').style.width = pct1 + '%';
  $('bpZoneS').textContent = '小';
  $('bpZoneM').style.left = pct1 + '%';
  $('bpZoneM').style.width = (pct2 - pct1) + '%';
  $('bpZoneM').textContent = '中';
  $('bpZoneL').style.left = pct2 + '%';
  $('bpZoneL').style.width = (100 - pct2) + '%';
  $('bpZoneL').textContent = '大';

  // Thumbs
  $('bpThumbL').style.left = pct1 + '%';
  $('bpThumbR').style.left = pct2 + '%';

  // Current window marker
  $('bpNowMarker').style.left = pxToPct(w) + '%';

  // Labels
  $('bpLabelLow').textContent = b1;
  $('bpLabelMid').textContent = `${b1}〜${b2}`;
  $('bpLabelHigh').textContent = b2;
}

function bindProfileUI() {
  // Tab buttons
  document.querySelectorAll('#profileTabs .sort-pill').forEach((p) => {
    p.addEventListener('click', () => {
      const v = p.dataset.profile;
      setProfileOverride(v === 'auto' ? null : v);
      applyEditorStyle();
      syncSettingsUI();
      renderTree();
    });
  });

  // Thumb drag
  const slider = $('bpSlider');
  const trackEl = slider.querySelector('.bp-track');
  let dragging = null; // 'L' | 'R' | null

  function onPointerMove(e) {
    if (!dragging) return;
    const rect = trackEl.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    let px = pctToPx(pct);
    let [b1, b2] = getProfileBreakpoints();
    if (dragging === 'L') {
      px = Math.max(BP_MIN, Math.min(px, b2 - 50)); // keep at least 50px gap
      setBreakpoints(px, b2);
    } else {
      px = Math.max(b1 + 50, Math.min(px, BP_MAX));
      setBreakpoints(b1, px);
    }
    renderProfileSelector();
    _checkProfileChange();
  }
  function onPointerUp() {
    if (dragging) {
      const el = dragging === 'L' ? $('bpThumbL') : $('bpThumbR');
      el.classList.remove('dragging');
    }
    dragging = null;
    document.removeEventListener('mousemove', onPointerMove);
    document.removeEventListener('mouseup', onPointerUp);
    document.removeEventListener('touchmove', onPointerMove);
    document.removeEventListener('touchend', onPointerUp);
  }
  function startDrag(thumb, e) {
    dragging = thumb;
    const el = thumb === 'L' ? $('bpThumbL') : $('bpThumbR');
    el.classList.add('dragging');
    document.addEventListener('mousemove', onPointerMove);
    document.addEventListener('mouseup', onPointerUp);
    document.addEventListener('touchmove', onPointerMove, { passive: false });
    document.addEventListener('touchend', onPointerUp);
    if (e.cancelable) e.preventDefault();
  }
  $('bpThumbL').addEventListener('mousedown', (e) => startDrag('L', e));
  $('bpThumbR').addEventListener('mousedown', (e) => startDrag('R', e));
  $('bpThumbL').addEventListener('touchstart', (e) => startDrag('L', e), { passive: false });
  $('bpThumbR').addEventListener('touchstart', (e) => startDrag('R', e), { passive: false });

  // Reset breakpoints to defaults
  const resetBtn = $('bpResetBtn');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      setBreakpoints(600, 1100);
      renderProfileSelector();
      _checkProfileChange();
      flashBtn(resetBtn, '✓ リセット');
    });
  }

  // Live update on resize
  window.addEventListener('resize', () => renderProfileSelector());
}
// Bind once when DOM ready (after wireEvents likely already ran by then)
setTimeout(bindProfileUI, 0);

// ── Tutorial ──
const TUTORIAL_STEPS = [
  { target: '#appTitle', text: 'ようこそ！\n左上の「MEMO」をクリックすると\nいつでも使い方を確認できます。' },
];

let tutorialIdx = 0;

function startTutorial() {
  tutorialIdx = 0;
  $('tutorialOverlay').classList.add('active');
  showTutorialStep();
  $('tutorialNext').addEventListener('click', nextTutorialStep);
  $('tutorialOverlay').addEventListener('click', (e) => {
    if (e.target === $('tutorialOverlay')) nextTutorialStep();
  });
}

function showTutorialStep() {
  const step = TUTORIAL_STEPS[tutorialIdx];
  const el = document.querySelector(step.target);
  if (!el) { nextTutorialStep(); return; }

  const rect = el.getBoundingClientRect();
  const pad = 6;
  const spot = $('tutorialSpotlight');
  spot.style.top = (rect.top - pad) + 'px';
  spot.style.left = (rect.left - pad) + 'px';
  spot.style.width = (rect.width + pad * 2) + 'px';
  spot.style.height = (rect.height + pad * 2) + 'px';

  $('tutorialText').textContent = step.text;
  $('tutorialStep').textContent = `${tutorialIdx + 1} / ${TUTORIAL_STEPS.length}`;
  $('tutorialNext').textContent = tutorialIdx === TUTORIAL_STEPS.length - 1 ? '始める' : '次へ';

  const box = $('tutorialBox');
  const boxW = 280;
  let bx = rect.left + rect.width / 2 - boxW / 2;
  let by = rect.bottom + pad + 12;
  if (by + 150 > window.innerHeight) by = rect.top - pad - 150;
  if (bx < 10) bx = 10;
  if (bx + boxW > window.innerWidth - 10) bx = window.innerWidth - boxW - 10;
  box.style.left = bx + 'px';
  box.style.top = by + 'px';
}

function nextTutorialStep() {
  tutorialIdx++;
  if (tutorialIdx >= TUTORIAL_STEPS.length) {
    $('tutorialOverlay').classList.remove('active');
    localStorage.setItem('memo_tutorial_done', '1');
    return;
  }
  showTutorialStep();
}

function applyTreeStyle() {
  const ta = $('treeArea');
  ta.style.fontSize = (getSetting('treeFontSize') || 11) + 'px';
  ta.style.lineHeight = getSetting('treeLineHeight') || 1.6;
}

function applyEditorStyle() {
  const ta = $('editorBody');
  ta.style.fontSize = (getSetting('fontSize') || 11) + 'px';
  ta.style.letterSpacing = (getSetting('letterSpacing') || 0) + 'px';
  ta.style.lineHeight = getSetting('lineHeight') || 1.65;
}

// ── Wire events ──
function wireEvents() {
  // MEMO title → toggle help
  $('appTitle').addEventListener('click', (e) => {
    e.stopPropagation();
    $('helpTooltip').classList.toggle('show');
  });
  document.addEventListener('click', (e) => {
    if (!$('helpTooltip').contains(e.target) && e.target !== $('appTitle')) {
      $('helpTooltip').classList.remove('show');
    }
  });

  // Header buttons
  $('btnAddFolder').addEventListener('click', () => addFolder(null));
  $('btnAddMemo').addEventListener('click', () => addMemo(null));
  $('btnDelete').addEventListener('click', () => {
    const id = getSelectedId();
    if (!id) return;
    const visible = getVisibleNodeIds();
    const idx = visible.indexOf(id);
    const nextId = visible[idx + 1] || visible[idx - 1] || null;
    sendToTrash(id);
    if (nextId) {
      const n = getNode(nextId);
      if (n?.type === 'memo') {
        selectMemo(nextId);
      } else {
        setSelectedId(nextId);
        $('editorEmpty').style.display = '';
        $('editorMain').classList.remove('visible');
      }
    } else {
      hideEditor();
      if (layout === 'fullscreen') showFsTree();
    }
  });
  $('btnBack').addEventListener('click', showFsTree);
  $('btnSettings').addEventListener('click', toggleSettings);
  $('btnSettingsClose').addEventListener('click', toggleSettings);
  $('settingsMask').addEventListener('click', toggleSettings);

  // Fix mobile keyboard: resize app to visual viewport and prevent scroll
  if (window.visualViewport) {
    const appEl = document.querySelector('.app');
    window.visualViewport.addEventListener('resize', () => {
      appEl.style.height = window.visualViewport.height + 'px';
      window.scrollTo(0, 0);
    });
    window.visualViewport.addEventListener('scroll', () => window.scrollTo(0, 0));
  }

  // Fix iOS scroll position after keyboard dismiss
  $('editorBody').addEventListener('blur', () => {
    const appEl = document.querySelector('.app');
    appEl.style.height = '';
    setTimeout(() => window.scrollTo(0, 0), 50);
  });

  // Keyboard navigation
  document.addEventListener('keydown', (e) => {
    // Escape: editor → back to tree (fullscreen), or blur editor
    if (e.key === 'Escape') {
      if (e.target === $('editorBody')) {
        $('editorBody').blur();
        if (layout === 'fullscreen') showFsTree();
        return;
      }
      return;
    }

    if (e.target === $('editorBody') || e.target.tagName === 'INPUT') return;

    // Enter: folder → toggle, memo → open + focus editor
    if (e.key === 'Enter') {
      const curId = getSelectedId();
      if (!curId) return;
      const n = getNode(curId);
      if (!n) return;
      e.preventDefault();
      if (n.type === 'folder') {
        toggleExpand(curId);
      } else {
        selectMemo(curId);
        showFsEditor();
        $('editorBody').focus();
      }
      return;
    }

    // Arrow up/down
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const visible = getVisibleNodeIds();
    if (visible.length === 0) return;
    const curId = getSelectedId();
    const curIdx = curId ? visible.indexOf(curId) : -1;
    let nextIdx;
    if (e.key === 'ArrowUp') {
      nextIdx = curIdx <= 0 ? visible.length - 1 : curIdx - 1;
    } else {
      nextIdx = curIdx >= visible.length - 1 ? 0 : curIdx + 1;
    }
    const nextId = visible[nextIdx];
    const n = getNode(nextId);
    if (n?.type === 'memo' && layout !== 'fullscreen') {
      selectMemo(nextId);
    } else {
      setSelectedId(nextId);
      document.querySelectorAll('.node-row.is-selected').forEach((r) => r.classList.remove('is-selected'));
      document.querySelector(`.node-row[data-id="${nextId}"]`)?.classList.add('is-selected');
      if (layout !== 'fullscreen') {
        $('editorEmpty').style.display = '';
        $('editorMain').classList.remove('visible');
      }
    }
    document.querySelector(`.node-row[data-id="${nextId}"]`)?.scrollIntoView({ block: 'nearest' });
  });

  // Layout pills in settings
  $('layoutPills').addEventListener('click', (e) => {
    const pill = e.target.closest('.sort-pill[data-layout]');
    if (pill) setLayout(pill.dataset.layout);
  });

  // Search
  $('searchInput').addEventListener('input', (e) => {
    searchQuery = e.target.value.trim();
    renderTree();
  });

  // Editor
  $('editorBody').addEventListener('input', () => {
    userIsTyping = true;
    clearTimeout(userTypingTimer);
    userTypingTimer = setTimeout(() => {
      userIsTyping = false;
      // Catch up tree render after typing stops
      renderTree();
      renderTrash();
    }, 2000);
    schedSave();
  });

  // Settings sliders
  $('slTreeFontSize').addEventListener('input', (e) => { setSetting('treeFontSize', +e.target.value); $('vTreeFontSize').textContent = e.target.value + 'px'; applyTreeStyle(); });
  $('slTreeLineHeight').addEventListener('input', (e) => { setSetting('treeLineHeight', +e.target.value); $('vTreeLineHeight').textContent = e.target.value; applyTreeStyle(); });
  $('slFontSize').addEventListener('input', (e) => { setSetting('fontSize', +e.target.value); $('vFontSize').textContent = e.target.value + 'px'; applyEditorStyle(); });
  $('slLetterSpacing').addEventListener('input', (e) => { setSetting('letterSpacing', +e.target.value); $('vLetterSpacing').textContent = e.target.value + 'px'; applyEditorStyle(); });
  $('slLineHeight').addEventListener('input', (e) => { setSetting('lineHeight', +e.target.value); $('vLineHeight').textContent = e.target.value; applyEditorStyle(); });

  // Presets
  for (let i = 1; i <= 3; i++) {
    $('presetSave' + i).addEventListener('click', () => savePreset(i));
    $('presetLoad' + i).addEventListener('click', () => loadPreset(i));
    $('presetName' + i).addEventListener('click', () => startPresetRename(i));
  }
  $('btnResetSettings').addEventListener('click', resetSettingsToDefaults);
  updatePresetUI();

  // Sort pills
  $('sortPills').addEventListener('click', (e) => {
    const pill = e.target.closest('.sort-pill[data-sort]');
    if (pill) {
      setSetting('sort', pill.dataset.sort);
      renderTree();
    }
  });

  // Trash header
  $('trashHeader').addEventListener('click', () => {
    trashOpen = !trashOpen;
    $('trashArrow').classList.toggle('open', trashOpen);
    renderTrash();
  });

  // Trash empty
  $('trashEmptyBtn').addEventListener('click', async (e) => {
    e.stopPropagation();
    if (Object.keys(getTrashEntries()).length === 0) return;
    if (!confirm('ゴミ箱の中身をすべて完全に削除しますか？')) return;
    // 完全削除前にスナップショットを撮っておく（復元 UI から戻せる保険）
    if (provider?.takeManualSnapshot) {
      try { await provider._sendSnapshot(Y.encodeStateAsUpdate(doc), 'pre-empty-trash', 'before emptying trash'); } catch {}
    }
    storeEmptyTrash();
  });

  // Backup / Restore UI
  $('btnOpenBackup').addEventListener('click', () => openBackupModal());
  $('backupClose').addEventListener('click', () => closeBackupModal());
  $('backupMask').addEventListener('click', () => closeBackupModal());
  $('btnReloadSnapshots').addEventListener('click', () => loadSnapshotList());
  $('btnTakeSnapshot').addEventListener('click', () => takeManualSnapshotAndReload());
  $('previewClose').addEventListener('click', () => closePreviewModal());
  $('previewMask').addEventListener('click', () => closePreviewModal());

  // Login
  $('loginBtn').addEventListener('click', () => startGoogleLogin());
  $('loginSkipBtn').addEventListener('click', () => { hideLoginOverlay(); updateUserUI(); });
  $('btnLoginFromSettings').addEventListener('click', () => startGoogleLogin());
  $('headerLoginBtn').addEventListener('click', () => startGoogleLogin());
  // Logout — inline confirm
  $('btnLogout').addEventListener('click', () => {
    $('btnLogout').style.display = 'none';
    $('logoutConfirm').style.display = '';
  });
  $('logoutNo').addEventListener('click', () => {
    $('logoutConfirm').style.display = 'none';
    $('btnLogout').style.display = '';
  });
  $('logoutYes').addEventListener('click', () => {
    signOut();
    location.reload();
  });

  // Upgrade
  $('btnUpgrade').addEventListener('click', () => upgradeToProPlan());
  $('upgradeModalBtn').addEventListener('click', () => { hideUpgradeModal(); upgradeToProPlan(); });
  $('upgradeModalClose').addEventListener('click', () => hideUpgradeModal());
  $('upgradeModal').addEventListener('click', (e) => { if (e.target === $('upgradeModal')) hideUpgradeModal(); });

  // Cancel plan — inline confirm
  $('btnCancelPlan').addEventListener('click', () => {
    $('btnCancelPlan').style.display = 'none';
    $('cancelConfirm').style.display = '';
  });
  $('cancelNo').addEventListener('click', () => {
    $('cancelConfirm').style.display = 'none';
    $('btnCancelPlan').style.display = '';
  });
  $('cancelYes').addEventListener('click', async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch('/api/stripe/cancel', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        $('cancelConfirm').style.display = 'none';
        const u = getUser();
        if (u) { u.cancelAtPeriodEnd = true; setUser(u); }
        updateUserUI();
      }
    } catch (e) {
      console.error('Cancel error:', e);
    }
  });

  // Import
  $('btnImport')?.addEventListener('click', async () => {
    $('importDialog').classList.add('open');
  });
  $('importFileBtn')?.addEventListener('click', async () => {
    try {
      await importFromFile(getDoc());
      $('importDialog').classList.remove('open');
      renderTree();
      renderTrash();
    } catch (e) {
      alert('インポートに失敗しました: ' + e.message);
    }
  });
  $('importCancelBtn')?.addEventListener('click', () => {
    $('importDialog').classList.remove('open');
  });
  // Export with cooldown (survives page refresh)
  const EXPORT_COOLDOWN = 60000;
  const exportBtn = $('btnExport');
  function updateExportBtn() {
    if (!exportBtn) return;
    const last = parseInt(localStorage.getItem('memo_export_at') || '0', 10);
    const remaining = EXPORT_COOLDOWN - (Date.now() - last);
    if (remaining > 0) {
      exportBtn.disabled = true;
      exportBtn.textContent = `次回${Math.ceil(remaining / 1000)}秒後に可能`;
      setTimeout(updateExportBtn, 1000);
    } else {
      exportBtn.disabled = false;
      exportBtn.textContent = 'データ書き出し';
    }
  }
  updateExportBtn();
  exportBtn?.addEventListener('click', () => {
    if (exportBtn.disabled) return;
    exportToFile(getDoc());
    localStorage.setItem('memo_export_at', String(Date.now()));
    updateExportBtn();
  });

  // Keyboard shortcuts
  document.addEventListener('keydown', (e) => {
    if (e.target.closest('textarea')) return;
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'n') { e.preventDefault(); addMemo(null); }
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'N') { e.preventDefault(); addFolder(null); }
    if ((e.ctrlKey || e.metaKey) && e.key === ',') { e.preventDefault(); toggleSettings(); }
  });

  // Split handle
  initSplitHandle();

  // Auto-clean trash (30 days)
  autoCleanTrash();
}

function autoCleanTrash() {
  const entries = getTrashEntries();
  const limit = 30 * 24 * 60 * 60 * 1000;
  const now = ts();
  Object.entries(entries).forEach(([id, entry]) => {
    if (now - entry.deletedAt > limit) permanentDeleteTrash(id);
  });
}

// Start
init();
